import { KatalComponent } from '../../shared/base';

/**
 * @component {kat-date-picker} KatalDatepicker Allows a user to select a date and provides a display for the selected date.
 * @example UnitedStatesLocale {"locale": "en-US", "label": "US English", "value": ""}
 * @example Disabled {"locale": "en-US", "label": "Disabled", "value": "", "disabled": "true"}
 * @example GermanLocale {"locale": "de-DE", "label": "German", "value": "01.01.2018"}
 * @example ChineseLocale {"locale": "zh-CN", "label": "Mandarin", "value": "2019/01/01", "script": ""}
 * @example IsoFormat {"locale": "en-US", "label": "Set by ISO String", "value": "2019-09-21T12:00:00.000Z", "script": ""}
 * @example DisabledWeekends {"locale": "en-US", "label": "Disabled Weekends",
 * "script": "
 *   document.querySelector(\"kat-date-picker\").isDateDisabled = function (date) {
 *     return (date.getDay() === 0 || date.getDay() === 6);
 *   };
 * "}
 * @event {{value: string, isoValue: string, dateObject: Date}} change Change event will fire when either date is updated. See event.detail.value, event.detail.isoValue, and event.detail.dateObject for the selected value
 * @event {{value: string}} invalid Fired when the entered value is not parseable as a Date. The invalid value will be attached to the event as event.detail.value
 * @status Production
 * @theme flo
 */
export class KatalDatePicker extends KatalComponent {
  static get observedAttributes() {
    return [
      'value',
      'date-value',
      'label',
      'locale',
      'name',
      'disabled',
      'katal-1-2-spacing-fixes',
    ];
  }

  constructor() {
    super([...KatalDatePicker.observedAttributes, 'is-date-disabled']);
    this.showing = false;
    this._calendarDateFormat = 'MM/DD/YYYY';
    this._skipNextAttributeChanged = false;
    this._dateObject = null;
  }

  connectedCallback() {
    super.connectedCallback();
    this.render();
    this.connectEvents();
  }

  attributeChangedCallback(attr, oldValue, newValue) {
    if (newValue === oldValue) {
      return;
    }

    if (this._skipNextAttributeChanged) {
      this._skipNextAttributeChanged = false;
    } else {
      const currentValue = this.value;
      if (attr === 'locale' && currentValue.length && oldValue && newValue) {
        const oldFormatString = getDateFormatFromLocale(oldValue);
        const newFormatString = getDateFormatFromLocale(newValue);
        const currentDate = createDateFromStringAndFormat(
          currentValue,
          oldFormatString
        );
        const newDateString = this.formatDateObject(
          currentDate,
          newFormatString
        );
        this.value = newDateString;
      } else if (attr === 'date-value') {
        this.dateValue = newValue;
      } else if (attr === 'value') {
        this.value = newValue;
      }
    }

    // Defer the first render until all properties have been upgraded
    if (this._propertiesHaveBeenUpgraded) {
      this.render();
    }
  }

  /**
   * @classprop {string | Date} value Set the value for the date picker. This will only emit an 'invalid' date if it is not well-formed. No 'change' event is
   * emitted if successfully set
   */
  set value(value) {
    if (
      value === null ||
      value === undefined ||
      !(
        value instanceof Date ||
        typeof value === 'string' ||
        typeof value === 'number'
      )
    ) {
      this._dateObject = null;
      this.removeAttribute('value');
      return;
    }

    if (value === '') {
      this._dateObject = null;
      this.setAttribute('value', '');
      return;
    }

    if (typeof value === 'string') {
      // Cast to integer if is number
      if (value.match(/^[0-9]+$/)) {
        value = new Date(parseInt(value, 10));
      } else if (value.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/)) {
        value = new Date(value);
      }
    } else if (typeof value === 'number') {
      value = new Date(value);
    }

    let dateObject = value;
    let formattedValue = value;

    // Try converting to format specified by locale
    if (value instanceof Date) {
      formattedValue = this.formatDateObject(value, this.placeholder);
    } else {
      dateObject = this.createDateFromStringAndPlaceholderFormat(value);
    }

    const oldValue = this.value;
    if (this._disabledDateFunc && this._disabledDateFunc(dateObject)) {
      this.render();
      const input = this.getElementsByTagName('input')[0];
      input.focus();
      return;
    }

    if (!this.isValidDateObject(dateObject)) {
      this.emitInvalidEvent(value);
      this.removeAttribute('value');
      this._dateObject = null;
      return;
    }

    this._dateObject = dateObject;
    // Skip next attribute changed logic because it would cause an infinite loop as it calls this setter when the value attribute changes.
    this._skipNextAttributeChanged = true;
    this.setAttribute('value', formattedValue);

    // Update the lastValue so the change event fires correctly.
    this.lastValue = this.value;
  }
  get value() {
    return this.getAttribute('value') || '';
  }

  /**
   * @classprop {string | Date} date-value Returns a standard Date object. Setting this attribute/property is exactly the same as setting "value". The
   * "date-value" attribute will not be kept up-to-date automatically.
   */
  get dateValue() {
    return this._dateObject;
  }
  set dateValue(value) {
    this.value = value;
  }

  /**
   * @classprop {string} locale Set the locale for the date picker -default is en-US
   */
  set locale(value) {
    this.setAttribute('locale', value);
  }

  get locale() {
    return this.getAttribute('locale') || 'en-US';
  }

  get placeholder() {
    return getDateFormatFromLocale(this.locale);
  }

  formatDateObject(date, formatString) {
    if (!(date instanceof Date)) {
      throw new Error('Cannot format an object that is not a Date');
    }

    let formattedDate = formatString;

    formattedDate = formattedDate.replace(
      'MM',
      this.padDate((date.getMonth() + 1).toString())
    );
    formattedDate = formattedDate.replace(
      'DD',
      this.padDate(date.getDate().toString())
    );
    formattedDate = formattedDate.replace(
      'YYYY',
      date.getFullYear().toString()
    );

    return formattedDate;
  }

  /**
   * We use a custom padStart method as opposed to the builtin str.padStart for broader browser support
   * @katalignore
   * @param {string} str
   */
  padDate(str) {
    if (str.padStart) {
      return str.padStart(2, '0');
    } else {
      let paddingString = '';
      for (let i = 0; i < Math.max(2 - str.length, 0); i++) {
        paddingString += '0';
      }
      return paddingString + str;
    }
  }

  /**
   * @classprop {string} label Set the Label for the input
   */
  set label(value) {
    this.setAttribute('label', value);
  }

  get label() {
    return this.getAttribute('label') || '';
  }

  /**
   * @classprop {string} name Set the name attribute for the input
   */
  set name(value) {
    this.setAttribute('name', value);
  }

  get name() {
    return this.getAttribute('name') || '';
  }

  /**
   * @classprop {boolean} disabled If true the datepicker will be disabled and unable to be interacted with.
   */
  get disabled() {
    return this.getBooleanAttribute('disabled');
  }
  set disabled(value) {
    this.setBooleanAttribute('disabled', value);
  }

  /**
   * @classprop {(date: Date) => boolean} is-date-disabled Sets the function used to determine which dates are disabled. Called for each date rendered in the
   * date picker to determine if the date should be disabled. If this function returns true the given date will be disabled. If the logic of this function is
   * changed the disabled state will only be displayed after the next time the user opens the date picker calendar. If instead you use this setter to set the
   * function again the calendar will be re-rendered immediately with the new logic.
   */
  get isDateDisabled() {
    return this._disabledDateFunc;
  }
  set isDateDisabled(func) {
    this._disabledDateFunc = func;
    const calendar = this.getElementsByTagName('kat-calendar')[0];
    if (calendar && calendar.render) {
      calendar.render();
    }
  }

  // Note: For the spacing attribute we just need the JSON metadata not the actual getter and setter.
  /**
   * @param {boolean} katal-1-2-spacing-fixes A special compatibility flag that when set removes extra margins around this component and sets its height to
   * the correct default. Note that this will become the default behavior in the next major release of Katal. Take a look at our service bulletin page for
   * more details.
   */
  get katal12SpacingFixes() {
    return this.getBooleanAttribute('katal-1-2-spacing-fixes');
  }
  set katal12SpacingFixes(value) {
    return this.setBooleanAttribute('katal-1-2-spacing-fixes', value);
  }

  show() {
    if (this.showing || this.disabled) {
      return;
    }
    this.showing = true;

    const calendar = this.querySelector('kat-calendar');
    const inputGroup = this.querySelector('kat-input-group');

    calendar.classList.add('show');
    calendar.render();

    inputGroup.classList.add('show');
  }

  hide() {
    if (this.static || !this.showing) {
      return;
    }
    this.showing = false;

    this.querySelector('kat-calendar').classList.remove('show');
    this.querySelector('kat-input-group').classList.remove('show');
  }

  connectEvents() {
    this.addEventListener('focusin', this.show);
    this.addEventListener('click', e => {
      this.querySelector('kat-input input').focus();
      this.show();
    });

    // Events bound to the inner input group
    const inputGroup = this.querySelector('kat-input-group');

    inputGroup.addEventListener('change', e => {
      e.stopImmediatePropagation();
      this.emitChangeEvent(e.detail.value);
    });

    this.addEventListener('focusout', e => {
      e.stopImmediatePropagation();
      const target = e.target;
      const relatedTarget = e.relatedTarget;

      const targetIsInsideComponent = target && this.contains(target);
      const relatedTargetInsideComponent =
        relatedTarget && this.contains(relatedTarget);

      if (targetIsInsideComponent && !relatedTargetInsideComponent) {
        this.hide();
      }
    });

    // Events bound to the inner calendar
    const calendar = this.querySelector('kat-calendar');
    calendar.addEventListener('change', e => {
      e.stopImmediatePropagation();

      // Receive the date event and render it using the date format
      const formattedDate = this.formatDateObject(
        e.detail.date,
        this.placeholder
      );

      this.emitChangeEvent(formattedDate);
      this.hide();
    });

    calendar.addEventListener('hide', e => {
      e.stopImmediatePropagation();
      this.hide();
    });

    calendar.addEventListener('focusin', e => {
      e.stopImmediatePropagation();
    });

    calendar.addEventListener('focusout', e => {
      e.stopImmediatePropagation();

      const relatedTarget = e.relatedTarget;
      const target = e.target;

      if (calendar.contains(relatedTarget)) {
        //do nothing
      } else if (!calendar.keyPressFocusSwitch) {
        this.hide();
      }
      calendar.keyPressFocusSwitch = false;
    });

    calendar.dayFormatter = this._dayFormatter.bind(this);
  }

  createDateFromStringAndPlaceholderFormat(dateString) {
    if (dateString.length === 0) {
      return new Date();
    }

    return createDateFromStringAndFormat(dateString, this.placeholder);
  }

  isValidDateObject(date) {
    try {
      return date instanceof Date && !isNaN(date);
    } catch (e) {
      return false;
    }
  }

  // Called when a user interaction changes the selected date
  emitChangeEvent(value) {
    if (value === this.lastValue) {
      return;
    }

    // Setting value sets this.lastValue
    this.value = value;

    const isoValue = this._dateObject ? this._dateObject.toISOString() : '';
    this.dispatchCustomEvent('change', {
      value,
      isoValue,
      dateObject: this._dateObject,
    });
  }

  emitInvalidEvent(value) {
    this.dispatchCustomEvent('invalid', { value });
  }

  _dayFormatter(dayEle, date) {
    if (!this._disabledDateFunc) {
      return;
    }

    if (this._disabledDateFunc(date)) {
      dayEle.classList.add('disabled');
    }
  }

  render() {
    const querySelectorOrCreateElement = (parent, selector, elementTag) => {
      let element = parent.querySelector(selector);

      if (!element) {
        element = document.createElement(elementTag);
        parent.appendChild(element);
      }

      return element;
    };

    const label = this.label;
    const locale = this.locale;
    const name = this.name;
    const placeholder = this.placeholder;

    const template = querySelectorOrCreateElement(this, 'div', 'div');

    const labelElement = querySelectorOrCreateElement(
      template,
      'label',
      'label'
    );
    labelElement.classList.add('date-label');
    labelElement.innerText = label;

    const inputGroup = querySelectorOrCreateElement(
      template,
      'kat-input-group',
      'kat-input-group'
    );
    // Placed height fixing attribute on input group
    if (this.hasAttribute('katal-1-2-spacing-fixes')) {
      inputGroup.setAttribute('katal-1-2-spacing-fixes', '');
    } else {
      inputGroup.removeAttribute('katal-1-2-spacing-fixes');
    }

    const icon = querySelectorOrCreateElement(
      inputGroup,
      'kat-icon',
      'kat-icon'
    );
    icon.name = 'calendar-alt';

    const input = querySelectorOrCreateElement(
      inputGroup,
      'kat-input',
      'kat-input'
    );
    input.type = 'text';
    input.placeholder = placeholder;
    input.name = name;
    input.value = this.value;
    if (this.disabled) {
      input.setAttribute('disabled', 'true');
    } else {
      input.removeAttribute('disabled');
    }

    const calendar = querySelectorOrCreateElement(
      this,
      'kat-calendar',
      'kat-calendar'
    );
    calendar.locale = locale;

    const dateObject = this.createDateFromStringAndPlaceholderFormat(
      this.value
    );
    const calendarFormattedString = this.formatDateObject(
      dateObject,
      this._calendarDateFormat
    );

    calendar.value = calendarFormattedString;
  }
}

KatalComponent.register('kat-date-picker', KatalDatePicker);

function splitDateString(string) {
  return string.split(/[^0-9A-Z]/g).filter(i => {
    return i.length > 0;
  });
}

function createDateFromStringAndFormat(dateString, format) {
  if (!dateString || !format) {
    return null;
  }

  const dateStringParts = splitDateString(dateString);
  const formatParts = splitDateString(format);

  if (dateStringParts.length !== 3 || formatParts.length !== 3) {
    return null;
  }

  let monthInteger, dayInteger, yearInteger;

  for (let i = 0; i < 3; i++) {
    const formatPart = formatParts[i];
    const dateStringPart = dateStringParts[i];
    if (formatPart === 'DD') {
      dayInteger = parseInt(dateStringPart, 10);
      if (dayInteger > 31) {
        dayInteger = NaN;
      }
    } else if (formatPart === 'MM') {
      monthInteger = parseInt(dateStringPart, 10);
      if (monthInteger > 12) {
        monthInteger = NaN;
      }
    } else if (formatPart === 'YYYY') {
      yearInteger = parseInt(dateStringPart, 10);
    }
  }

  return new Date(yearInteger, monthInteger - 1, dayInteger);
}

function getDateFormatFromLocale(locale) {
  // Until "en-AE" is officially supported in all browsers, use "en-NZ" to get the date format
  // "en-AE" and "en-NZ" both use DD/MM/YYYY
  // See https://t.corp.amazon.com/KAT-1589 for more information
  if (locale === 'en-AE') {
    locale = 'en-NZ';
  }

  // Create a temporary date for January 2nd, 2018
  const tempDate = new Date(Date.UTC(2018, 0, 2, 0, 0, 0));

  // Format this date into a string as if it were being displayed in the UTC time zone
  const formattedString = tempDate
    .toLocaleDateString(locale, {
      timeZone: 'UTC',
    })
    .replace(/\u200E/g, ''); // IE/Edge likes to put a Left-to-right character inside the formatted date.

  // Split the string on all non-numeric characters, filter out the ones that are zero-length (control characters)
  const splitFormattedString = splitDateString(formattedString);

  if (splitFormattedString.length !== 3) {
    throw new Error(
      'cannot parse string; there are not three pieces when split'
    );
  }

  for (let i = 0; i < 3; i++) {
    const formattedItem = parseInt(splitFormattedString[i]);
    if (formattedItem === 1) {
      splitFormattedString[i] = 'MM';
    } else if (formattedItem === 2) {
      splitFormattedString[i] = 'DD';
    } else if (formattedItem === 2018) {
      splitFormattedString[i] = 'YYYY';
    } else {
      throw new Error('could not reformat date string');
    }
  }

  return formattedString
    .replace('2018', 'YYYY')
    .replace('01', 'MM')
    .replace('1', 'MM')
    .replace('02', 'DD')
    .replace('2', 'DD');
}
